import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Settings, Zap, BarChart3, FileText, Shield, Database, Bot, Archive, Crown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface Workspace {
  id: string;
  name: string;
  plan?: {
    code: string;
    name: string;
  };
}

interface ModuleDefinition {
  code: string;
  name: string;
  description: string;
  parent_module?: string;
  plan_required: string;
  category: string;
  icon: string;
  is_active: boolean;
}

interface WorkspaceModule {
  module_code: string;
  submodule_code?: string;
  enabled: boolean;
  enabled_at: string;
  plan_required: string;
}

interface ModuleManagementModalProps {
  workspace: Workspace | null;
  isOpen: boolean;
  onClose: () => void;
}

export function ModuleManagementModal({ workspace, isOpen, onClose }: ModuleManagementModalProps) {
  const queryClient = useQueryClient();
  const [pendingChanges, setPendingChanges] = useState<Record<string, boolean>>({});

  // Fetch workspace current modules with proper error handling
  const { data: workspaceData, isLoading } = useQuery({
    queryKey: ['workspace-modules', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return { modules: [], plan: null };
      
      const { data, error } = await supabase.functions.invoke('workspace-modules', {
        body: {
          action: 'get',
          workspace_id: workspace.id
        }
      });
      
      if (error) {
        console.error('Error fetching workspace modules:', error);
        throw error;
      }
      
      return {
        modules: data?.modules || [],
        plan: data?.plan || null
      };
    },
    enabled: !!workspace?.id && isOpen,
    retry: 1
  });

  const modules = workspaceData?.modules || [];
  const workspacePlan = workspaceData?.plan || workspace?.plan;

  // Update modules mutation
  const updateModulesMutation = useMutation({
    mutationFn: async (updates: any[]) => {
      if (!workspace?.id) throw new Error('No workspace selected');
      
      const { data, error } = await supabase.functions.invoke('workspace-modules', {
        body: {
          action: 'update_overrides',
          workspace_id: workspace.id,
          module_overrides: updates
        }
      });
      
      if (error) {
        console.error('Error updating modules:', error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      toast.success('Modules updated successfully');
      queryClient.invalidateQueries({ queryKey: ['workspace-modules', workspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['entitlements'] });
      setPendingChanges({});
      onClose();
    },
    onError: (error: any) => {
      toast.error(`Failed to update modules: ${error.message}`);
    }
  });

  const getModuleIcon = (iconCode: string) => {
    const iconMap: Record<string, React.ComponentType<any>> = {
      settings: Settings,
      automation: Zap,
      analytics: BarChart3,
      reports: FileText,
      security: Shield,
      database: Database,
      ai: Bot,
      storage: Archive,
      enterprise: Crown
    };
    
    const IconComponent = iconMap[iconCode] || Settings;
    return <IconComponent className="h-4 w-4" />;
  };

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'enterprise': return 'bg-purple-100 text-purple-800';
      case 'customer': return 'bg-blue-100 text-blue-800';
      case 'trial': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isModuleEnabled = (moduleCode: string, subModuleCode?: string) => {
    const key = subModuleCode ? `${moduleCode}.${subModuleCode}` : moduleCode;
    
    if (key in pendingChanges) {
      return pendingChanges[key];
    }
    
    const existing = modules.find((m: any) => 
      m.id === moduleCode && m.enabled
    );
    
    return existing?.enabled || false;
  };

  const canToggleModule = (module: any) => {
    const userPlan = workspacePlan?.code || 'trial';
    const planHierarchy = { trial: 0, customer: 1, enterprise: 2 };
    return planHierarchy[userPlan as keyof typeof planHierarchy] >= planHierarchy[module.plan_required as keyof typeof planHierarchy];
  };

  const handleToggleModule = (moduleCode: string, subModuleCode?: string, newValue?: boolean) => {
    const key = subModuleCode ? `${moduleCode}.${subModuleCode}` : moduleCode;
    const currentValue = isModuleEnabled(moduleCode, subModuleCode);
    const finalValue = newValue !== undefined ? newValue : !currentValue;
    
    setPendingChanges(prev => ({
      ...prev,
      [key]: finalValue
    }));
  };

  const handleSaveChanges = () => {
    const updates = Object.entries(pendingChanges).map(([key, enabled]) => {
      return {
        module_id: key,
        enabled
      };
    });
    
    updateModulesMutation.mutate(updates);
  };

  // Group modules by category - simple grouping
  const modulesByCategory: Record<string, any[]> = {
    'Core': modules.filter((m: any) => ['billing_management', 'customer_portal'].includes(m.id)),
    'Analytics': modules.filter((m: any) => ['analytics_reporting'].includes(m.id)),
    'Automation': modules.filter((m: any) => ['automation_workflows', 'api_integrations'].includes(m.id)),
    'Security': modules.filter((m: any) => ['advanced_security'].includes(m.id)),
    'Management': modules.filter((m: any) => ['multi_tenant'].includes(m.id)),
  };

  // Filter out empty categories
  Object.keys(modulesByCategory).forEach(key => {
    if (modulesByCategory[key].length === 0) {
      delete modulesByCategory[key];
    }
  });

  if (!workspace) return null;

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Module Management</span>
          </DialogTitle>
          <DialogDescription>
            Enable or disable features for <strong>{workspace.name}</strong> (Plan: {workspace.plan?.name || 'Trial'})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Plan Information */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Current Plan</CardTitle>
                <Badge className={getPlanBadgeColor(workspacePlan?.code || 'trial')}>
                  {workspacePlan?.name || 'Trial'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Some modules may require plan upgrades. Contact support to upgrade your plan.
              </p>
            </CardContent>
          </Card>

          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="animate-pulse space-y-2">
                      <div className="h-4 bg-muted rounded w-1/3"></div>
                      <div className="h-3 bg-muted rounded w-2/3"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Main Modules */}
              {Object.entries(modulesByCategory).map(([category, categoryModules]) => {
                if (categoryModules.length === 0) return null;
                
                return (
                  <div key={category}>
                    <h3 className="text-lg font-semibold mb-4">{category}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {categoryModules.map((module: any) => {
                        const isEnabled = isModuleEnabled(module.id);
                        const canToggle = canToggleModule(module);
                        
                        return (
                          <Card key={module.id} className={!canToggle ? 'opacity-60' : ''}>
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  {getModuleIcon('settings')}
                                  <CardTitle className="text-base">{module.name}</CardTitle>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {module.plan_required !== 'trial' && (
                                    <Badge variant="outline" className="text-xs">
                                      {module.plan_required}
                                    </Badge>
                                  )}
                                  <Switch
                                    checked={isEnabled}
                                    onCheckedChange={(checked) => handleToggleModule(module.id, undefined, checked)}
                                    disabled={!canToggle}
                                  />
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <CardDescription className="text-sm">
                                {module.description}
                              </CardDescription>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => {
              setPendingChanges({});
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveChanges}
            disabled={!hasPendingChanges || updateModulesMutation.isPending}
          >
            {updateModulesMutation.isPending ? 'Saving...' : `Save Changes ${hasPendingChanges ? `(${Object.keys(pendingChanges).length})` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}